import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { ChartBaseDirective } from '@client/app/modules/charts/directives/chart-base.directive';
import { DEFAULT_CHART_SETTINGS } from '@client/app/modules/charts/constants/Chart.constant';
import { LegendCursorService } from '@client/app/modules/charts/services/LegendCursor.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'mon-chart-line',
    templateUrl: './line.component.html',
    standalone: true,
    imports: [
        ChartBaseDirective,
        CommonModule,
    ],
})
export class LineComponent implements OnInit, OnChanges {
    @Input() labels: any[] = [];
    @Input() data: any[] = [];
    @Input() series: any[] = [];
    @Input() override: any[] = [];
    @Input() options: any = {};
    @Input() height: any = '';
    @Input() colors: any[] = [];
    @Input() emptyDataMessage: string = '';
    @Input() progress: any = false;

    getDataComplete: boolean = true;

    constructor (
        private legendCursorService: LegendCursorService,
        private translateService: TranslateService,
    ) {}

    ngOnInit (): void {
        this.progress = this.progress || false;
        this.getDataComplete = true;
        this.isProgressRunning();
        this.emptyDataMessage = this.emptyDataMessage || this.translateService.getString('No data present');
        this.setupLineChart();
    }

    ngOnChanges (): void {
        this.isProgressRunning();
    }

    setupLineChart (): void {
        this.colors = this.colors && this.colors.length > 0 ? this.colors : [
            DEFAULT_CHART_SETTINGS.CHART.COLORS.LIGHT_BLUE,
            DEFAULT_CHART_SETTINGS.CHART.COLORS.BLUE,
            DEFAULT_CHART_SETTINGS.CHART.COLORS.LIGHT_GRAY,
            DEFAULT_CHART_SETTINGS.CHART.COLORS.GREEN,
            DEFAULT_CHART_SETTINGS.CHART.COLORS.ORANGE,
            DEFAULT_CHART_SETTINGS.CHART.COLORS.GRAY,
            DEFAULT_CHART_SETTINGS.CHART.COLORS.DARK_GRAY,
        ];
        this.override = this.override || [];
        this.options = this.options || {
            scales: {
                xAxes: [{
                    gridLines: {
                        display: false,
                    },
                }],
            },
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    boxWidth: 10,
                    padding: 5,
                    fontSize: 11,
                    fontFamily: 'Proxima Nova',
                    fontColor: '#A6A6A6',
                },
            },
            tooltips: {
                enabled: true,
                mode: 'x-axis',
                bodySpacing: 5,
            },
            hover: { mode: 'x-axis' },
        };

        if (this.options.legend) {
            this.options.legend.labels = {
                boxWidth: 15,
                padding: 15,
                fontSize: 11,
            };
        }
        this.options = this.legendCursorService.setHoverState(this.options);

        const pushToOverride = this.override.length === 0;
        if (this.options.scales && this.options.scales.yAxes && pushToOverride) {
            this.options.scales.yAxes.forEach((data: any, index: number) => {
                this.override.push(this.chartDataSet(data.id, this.toRgb(this.colors[(index % this.colors.length)])));
            });
        } else if (!(this.options.scales && this.options.scales.yAxes && this.options.scales.yAxes.length > 0)) {
            if (!this.options.scales) {
                this.options.scales = {};
            }
            if (!this.options.scales.yAxes) {
                this.options.scales.yAxes = [];
            }

            this.series.forEach((data: any, index: number) => {
                this.options.scales.yAxes.push({
                    id: data,
                    type: 'linear',
                    display: (index === 0),
                    position: 'left',
                    ticks: {
                        beginAtZero: true,
                        suggestedMax: 4,
                        maxTicksLimit: 5,
                    },
                });

                if (pushToOverride) {
                    this.override.push(this.chartDataSet(data, this.toRgb(this.colors[(index % this.colors.length)])));
                }
            });
        }
    }

    toRgb (color: string): string {
        if (/^#[0-9A-F]{6}$/i.test(color)) {
            let c = color.substring(1).split('');
            if (c.length === 3) {
                c = [c[0], c[0], c[1], c[1], c[2], c[2]];
            }
            const colorValue = parseInt('0x' + c.join(''), 16);
            return [(colorValue >> 16) & 255, (colorValue >> 8) & 255, colorValue & 255].join(',');
        }

        return color;
    }

    chartDataSet (ID: string, color: string): any {
        return {
            yAxisID: ID,
            fill: false,
            borderWidth: 2,
            pointHoverRadius: 4,
            pointRadius: 3,
            pointBackgroundColor: 'rgba(' + color + ', 1)',
            pointHoverBorderColor: '#fff',
            backgroundColor: 'rgba(' + color + ', 0.2)',
            borderColor: 'rgba(' + color + ', 1)',
            pointBorderColor: 'rgba(' + color + ', 1)',
            pointHoverBackgroundColor: 'rgba(' + color + ', 1)',
            pointHitRadius: 15,
            tension: 0.1,
        };
    }

    areAllDataRowsEmpty (): boolean {
        if (!Array.isArray(this.data)) {
            return true;
        }
        for (let i = 0; i < this.data.length; i++) {
            if (Array.isArray(this.data[i])) {
                if (this.data[i].length > 0) {
                    return false;
                }
            } else {
                return false;
            }
        }

        return true;
    }

    isProgressRunning (): void {
        if (this.progress !== false && this.progress !== undefined && this.progress !== null) {
            this.getDataComplete = false;
            if (this.progress.finally) {
                this.progress.finally(() => {
                    this.getDataComplete = true;
                });
            }
        }
    }
}
